import React from 'react';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../PageHeader';

const cards = [
  {
    title: 'PARAMETRES DU CYCLE MUSCULATION',
    description: 'Gérer les ateliers, groupes musculaires, mappings muscle ↔ ateliers et le mode guidé ou expert.',
    to: '/apps/carnet-muscu/settings',
    accent: 'linear-gradient(135deg, #f6efff 0%, #fcf8ff 100%)',
    borderColor: 'rgba(133,84,161,0.18)',
    iconBg: 'linear-gradient(135deg, rgba(133,84,161,0.14) 0%, rgba(214,192,255,0.24) 100%)',
    icon: <SettingsRoundedIcon sx={{ fontSize: 30 }} />
  },
  {
    title: 'LANCER LE CARNET DE SUIVI POUR MA CLASSE',
    description: 'Choisir une classe et générer les QR personnalisés pour donner accès aux carnets de muscu à vos élèves.',
    to: '/apps/carnet-muscu/classes',
    accent: 'linear-gradient(135deg, #fff4ea 0%, #fffaf5 100%)',
    borderColor: 'rgba(210,132,62,0.16)',
    iconBg: 'linear-gradient(135deg, rgba(210,132,62,0.12) 0%, rgba(255,209,168,0.22) 100%)',
    icon: <PlayCircleOutlineRoundedIcon sx={{ fontSize: 30 }} />,
    useQrIcon: true
  },
  {
    title: 'VOIR LE CARNET D’UN ELEVE',
    description: 'Choisir une classe puis un élève pour consulter la synthèse complète de son carnet.',
    to: '/apps/carnet-muscu/suivi-eleves',
    accent: 'linear-gradient(135deg, #f4f8ea 0%, #fbfdf5 100%)',
    borderColor: 'rgba(103,143,54,0.16)',
    iconBg: 'linear-gradient(135deg, rgba(103,143,54,0.14) 0%, rgba(190,220,138,0.2) 100%)',
    icon: <VisibilityRoundedIcon sx={{ fontSize: 30 }} />
  },
  {
    title: 'SAISIR UNE SEANCE TYPE ELEVE',
    description: 'Accéder rapidement à la saisie d’une séance de musculation type élève.',
    to: '/carnet-muscu/ajouter',
    accent: 'linear-gradient(135deg, #eef7ff 0%, #f8fbff 100%)',
    borderColor: 'rgba(51,120,220,0.16)',
    iconBg: 'linear-gradient(135deg, rgba(51,120,220,0.12) 0%, rgba(156,205,255,0.2) 100%)',
    icon: <FitnessCenterRoundedIcon sx={{ fontSize: 30 }} />
  }
];

export default function CarnetMuscuHome() {
  const navigate = useNavigate();

  const cardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: { xs: 3, sm: 4 },
    boxShadow: '0 10px 22px rgba(32,24,64,0.08)',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 14px 30px rgba(32,24,64,0.12)'
    }
  };

  return (
    <Box sx={{ px: { xs: 1.25, sm: 3 }, py: { xs: 1.5, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="Carnet Muscu"
        subtitle="Accueil prof pour lancer une séance, consulter un carnet élève, gérer le cycle muscu ou saisir une séance."
        backTo="/apps"
        homeTo="/"
      />

      <Grid container spacing={{ xs: 1.25, sm: 2 }}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} lg={4} key={card.title}>
            <Paper
              role="button"
              tabIndex={0}
              onClick={() => navigate(card.to)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(card.to);
                }
              }}
              elevation={0}
              sx={{
                ...cardSx,
                background: card.accent,
                border: `1px solid ${card.borderColor}`
              }}
            >
              <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="flex-start">
                <Box
                  sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: { xs: 2.5, sm: 3 },
                    background: card.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}
                >
                  {card.useQrIcon ? (
                    <QrCode2RoundedIcon sx={{ fontSize: 30 }} />
                  ) : (
                    card.icon
                  )}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      mb: 0.35,
                      fontSize: { xs: '0.98rem', sm: '1.05rem' },
                      lineHeight: 1.2
                    }}
                  >
                    {card.title}
                  </Typography>

                  <Typography
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.84rem', sm: '0.92rem' },
                      lineHeight: 1.32
                    }}
                  >
                    {card.description}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

    </Box>
  );
}